import * as React from "react";
import { Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import type { TooltipProps } from "recharts";

export type ChartConfig = Record<string, { label: string; color: string }>;

interface ChartContainerProps {
  config: ChartConfig;
  className?: string;
  children: React.ReactElement;
}

/**
 * Wraps recharts in a ResponsiveContainer and injects CSS variables
 * for each config key as --color-{key}.
 */
export function ChartContainer({
  config,
  className,
  children,
}: ChartContainerProps) {
  const style = React.useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [key, value] of Object.entries(config)) {
      vars[`--color-${key}`] = value.color;
    }
    return vars;
  }, [config]);

  return (
    <div className={className} style={style}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

type RechartsValueType = number | string | (number | string)[];
type RechartsNameType = number | string;

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: TooltipProps<RechartsValueType, RechartsNameType>["payload"];
  label?: string;
  config: ChartConfig;
  labelFormatter?: (value: string) => string;
}

function ChartTooltipContentInner({
  active,
  payload,
  label,
  config,
  labelFormatter,
}: ChartTooltipContentProps) {
  if (!active || !payload?.length) return null;

  const displayLabel = labelFormatter && label ? labelFormatter(label) : label;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      {displayLabel && (
        <div className="mb-1.5 font-medium text-primary">{displayLabel}</div>
      )}
      <div className="flex flex-col gap-1">
        {payload.map((entry) => {
          const key = entry.dataKey as string;
          const cfg = config[key];
          if (!cfg) return null;
          return (
            <div key={key} className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ background: cfg.color }}
              />
              <span className="text-secondary">{cfg.label}</span>
              <span className="ml-auto font-medium text-primary tabular-nums">
                {entry.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ChartTooltipProps {
  config: ChartConfig;
  labelFormatter?: (value: string) => string;
}

export function ChartTooltip({ config, labelFormatter }: ChartTooltipProps) {
  return (
    <RechartsTooltip
      cursor={false}
      content={({ active, payload, label }) => (
        <ChartTooltipContentInner
          active={active}
          payload={payload}
          label={label as string}
          config={config}
          labelFormatter={labelFormatter}
        />
      )}
    />
  );
}
